import React, {
   ChangeEvent,
   CSSProperties,
   FocusEvent,
   Ref, useCallback,
   useContext,
   useImperativeHandle,
   useRef
} from 'react';
import { defined } from '@unifocus/js-utils';
import { convertSize } from '../../../../utils/StyleUtils';
import { FormContext, generateFormFieldId } from '../../form';
import CloseCircleFilled from '@ant-design/icons/CloseCircleFilled';

/**
 * {@docCategory TextArea}
 */
export interface TextAreaProps {
   /**
    * The name of the input item.
    */
   name?: string;

   /**
    * The value.
    */
   value?: string;

   /**
    * The placeholder text for the input item.
    */
   placeholder?: string;

   /**
    * Sets the input item to read-only.
    * @defaultvalue false
    */
   readOnly?: boolean;

   /**
    * Set the input item to disabled.
    * @defaultvalue false
    */
   disabled?: boolean;

   /**
    * Sets the width.
    */
   width?: string | number;

   /**
    * Sets the min width.
    */
   minWidth?: string | number;

   /**
    * Sets the max width.
    */
   maxWidth?: string | number;

   /**
    * Determines if a border should be shown around the component.
    * @defaultvalue true
    */
   border?: boolean;

   /**
    * Determines if the component should have internal padding.
    * @defaultvalue true
    */
   padding?: boolean;

   /**
    * Enable the clear feature.
    * @defaultvalue false
    */
   allowClear?: boolean;

   /**
    * The number of rows to display.
    */
   rows?: number;

   /**
    * Sets the maximum number of characters that can be entered.
    */
   maxLength?: number;

   /**
    * Set this to focus on first render
    */
   autoFocus?: boolean;

   /**
    * Whether or not to trigger change events on blur
    * @defaultvalue false
    */
   triggerOnBlur?: boolean;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * Callback called when the value is changed.
    * @param value - The value
    */
   onChange?: (value?: string) => void;

   /**
    * Callback called when the component loses focus.
    * @param event - The focus event object
    */
   onBlur?: (event: FocusEvent<HTMLTextAreaElement>) => void;

   /**
    * Callback called when the component gains focus.
    * @param event - The focus event object
    */
   onFocus?: (event: FocusEvent<HTMLTextAreaElement>) => void;

   /**
    * Function called when the user presses the enter key.
    */
   onPressEnter?: React.KeyboardEventHandler<HTMLTextAreaElement>;

   /**
    * Function called when the user presses a key.
    */
   onKeyDown?: React.KeyboardEventHandler<HTMLTextAreaElement>;
}

const ENTER_KEY_CODE = 13;

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
   (props: TextAreaProps, ref: Ref<HTMLTextAreaElement>) => {

      const inputRef = useRef<HTMLTextAreaElement>();
      const value = defined(props.value) ? props.value : '';
      const showClear = props.allowClear && value;
      const showBorder = props.border === undefined ? true : props.border;
      const padding = props.padding === undefined ? true : props.padding;
      const classNames = ['uf-text-area'];
      const { triggerOnBlur, onBlur, onChange, disabled, readOnly } = props;

      if (showBorder) {
         classNames.push('uf-text-area-bordered');
      }

      if (props.className) {
         classNames.push(props.className);
      }

      const onChangeHandler = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
         if (onChange && !triggerOnBlur) {
            const value = event.target.value;

            onChange(value.trim().length === 0 ? undefined : value);
         }
      },[onChange, triggerOnBlur]);

      const onBlurHandler = useCallback((event: FocusEvent<HTMLTextAreaElement>) => {
         const value = event.target.value.trim().length === 0 ? undefined : event.target.value;
         const cancelButtonCausedBlur = (event.relatedTarget && (event.relatedTarget as HTMLElement).getAttribute('data-iscancelbutton'));

         if (!cancelButtonCausedBlur && onChange && triggerOnBlur) {
            onChange(value);
         }

         if (onBlur) {
            onBlur(event);
         }
      },[triggerOnBlur, onChange, onBlur]);

      const handleKeyDown = (props: TextAreaProps) => (event: React.KeyboardEvent<HTMLTextAreaElement>) => {

         if (event.keyCode === ENTER_KEY_CODE) {

            if (props.onPressEnter) {
               props.onPressEnter(event);
            }
         }

         if (props.onKeyDown) {
            props.onKeyDown(event);
         }
      };

      const onClear = useCallback(() => {
         if (disabled || readOnly) {
            return;
         }

         if (onChange) {
            onChange(undefined);
         }

         if (inputRef.current) {
            inputRef.current.focus();
         }
      },[onChange, disabled, readOnly]);

      useImperativeHandle(ref, () => ({
         focus: () => {
            if (inputRef.current) {
               inputRef.current.focus();
            }
         },
         blur: () => {
            if (inputRef.current) {
               inputRef.current.blur();
            }
         },
         select: () => {
            if (inputRef.current) {
               inputRef.current.select();
            }
         },
         setSelectionRange: (selectionStart: number, selectionEnd: number, selectionDirection?: 'forward' | 'backward' | 'none') => {
            if (inputRef.current) {
               inputRef.current.setSelectionRange(selectionStart, selectionEnd, selectionDirection);
            }
         },
      } as any));

      const style = {
         width: convertSize(props.width, '250px'),
         minWidth: convertSize(props.minWidth, '60px'),
         maxWidth: convertSize(props.maxWidth),
         ...props.style
      }

      const formContext = useContext(FormContext);
      const inputId = generateFormFieldId(formContext.name, props.name);

      return (
         <div
            className={classNames.join(' ')}
            style={style}
         >
            <textarea
               id={inputId}
               ref={inputRef as any}
               name={props.name}
               value={value}
               placeholder={props.placeholder}
               readOnly={props.readOnly}
               disabled={props.disabled}
               rows={props.rows}
               maxLength={props.maxLength}
               autoFocus={props.autoFocus}
               className={`uf-text-area-control ${padding ? 'uf-text-area-control-padding' : ''} ${showClear ? 'uf-text-area-control-clear' : ''}`}
               onChange={onChangeHandler}
               onBlur={onBlurHandler}
               onFocus={props.onFocus}
               onKeyDown={handleKeyDown(props)}
            />
            {showClear && !props.disabled ?
               <span className={`uf-text-area-clear`} onClick={onClear} onFocus={(event) => {event.preventDefault()}}>
                  <CloseCircleFilled/>
               </span> :
               null
            }
         </div>
      );
   });
